import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChevronDown, FileText, Phone, Search } from "lucide-react";
import { BACKEND } from "../lib/api";
import { SdsDetail, GhsPictogram } from "../components/SdsDetail";

const Item = ({ c, token, open, onToggle }) => (
  <div className="panel overflow-hidden" data-testid={`sds-item-${c.id}`}>
    <button className="flex w-full items-center gap-3 p-4 text-left" onClick={onToggle} data-testid={`sds-item-toggle-${c.id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-bold leading-tight">{c.product_name}</p>
        <p className="text-xs text-slate-400">{c.storage_area} {c.sds?.signal_word ? `· ${c.sds.signal_word}` : ""}</p>
      </div>
      <div className="hidden gap-3 sm:flex">{(c.sds?.pictograms || []).slice(0, 3).map((p) => <GhsPictogram key={p} code={p} size={22} />)}</div>
      <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <div className="space-y-3 border-t border-slate-800 p-4">
        <SdsDetail c={c} />
        {c.has_sds ? <a href={`${BACKEND}/api/public/sds/${token}/${c.id}`} target="_blank" rel="noreferrer" className="flex h-12 items-center justify-center gap-2 rounded-lg font-semibold text-white" style={{ background: "#0F172A" }} data-testid={`sds-open-pdf-${c.id}`}><FileText className="h-5 w-5" />Open full safety data sheet</a>
          : <p className="rounded-lg border border-red-500 p-3 text-sm font-semibold text-red-300" data-testid={`sds-missing-${c.id}`}>No SDS loaded for this product - tell your supervisor now.</p>}
      </div>
    )}
  </div>
);

export default function SdsPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => { axios.get(`${BACKEND}/api/public/sds/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load safety data sheets")); }, [token]);
  const shown = (data?.chemicals || []).filter((c) => !q || [c.product_name, c.chemical_name, c.cas_number, c.storage_area, c.un_number].join(" ").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="min-h-screen bg-ink text-slate-100" data-testid="sds-public-page">
      <header className="sticky top-0 z-10 px-4 pb-4 pt-5 text-white shadow-lg" style={{ background: "#B91C1C" }}>
        <p className="text-xs font-semibold uppercase tracking-widest opacity-90">EP Group · Safety Data Sheets</p>
        <h1 className="text-2xl font-black leading-tight" data-testid="sds-site-name">{data?.site || "Chemical safety"}</h1>
        <a href="tel:999" className="mt-3 flex h-12 items-center justify-center gap-2 rounded-lg bg-white text-lg font-black" style={{ color: "#B91C1C" }} data-testid="sds-call-999"><Phone className="h-5 w-5" />Emergency? Call 999</a>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-3.5 h-5 w-5 text-black/40" />
          <input className="h-12 w-full rounded-lg border-0 bg-white pl-10 text-base text-black placeholder:text-black/40" placeholder="Product, chemical, CAS or location" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sds-search" />
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-3 p-4">
        {err && <p className="rounded-lg border border-red-500 p-4 font-semibold text-red-300" data-testid="sds-error">{err}</p>}
        {!data && !err && <p className="text-center text-slate-500">Loading...</p>}
        {data && <p className="text-xs text-slate-500" data-testid="sds-count">{shown.length} of {data.chemicals.length} products · tap a product for first aid and spill steps</p>}
        {shown.map((c) => <Item key={c.id} c={c} token={token} open={open === c.id} onToggle={() => setOpen(open === c.id ? null : c.id)} />)}
        {data && !shown.length && <p className="text-center text-slate-500">No product matches "{q}".</p>}
        <p className="pt-4 text-center text-[11px] text-slate-400">Read-only list kept up to date by your site's H&amp;S team. If something looks wrong, tell your supervisor.</p>
      </main>
    </div>
  );
}
